import { Bike, Check, Gift, ImageOff, Package, Pencil, Percent, Pizza, Plus, Trash2, Wallet, X } from "lucide-react";
import { useState } from "react";

import { miniaturaDe } from "@/utils/miniatura";

import { BRL, Dinheiro, Numero, Secao, TIPOS_PREMIO } from "./comum";
import EscolherProduto from "./EscolherProduto";

const novoId = () => Math.random().toString(36).slice(2, 12);

// Os tipos de prêmio como blocos para escolher (24/10/2026: "fluxo mais intuitivo")
const TIPOS = [
  { tipo: "produto", icone: Package, cor: "from-orange-500 to-amber-500", sub: "Um item do cardápio" },
  { tipo: "pizza", icone: Pizza, cor: "from-red-500 to-orange-500", sub: "Um tamanho, o sabor que quiser" },
  { tipo: "desconto_valor", icone: Wallet, cor: "from-emerald-500 to-teal-500", sub: "Valor fixo no pedido" },
  { tipo: "desconto_pct", icone: Percent, cor: "from-sky-500 to-blue-600", sub: "Porcentagem do pedido" },
  { tipo: "frete", icone: Bike, cor: "from-violet-500 to-fuchsia-500", sub: "Entrega sem taxa" },
];
const doTipo = (tipo) => TIPOS.find((x) => x.tipo === tipo) || TIPOS[0];

/** O texto do prêmio no cartão — o mesmo de `fidelidade/regras.texto_premio`. */
export function textoPremio(p) {
  if (!p) return "";
  const reais = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: Number(v) % 1 ? 2 : 0 })}`;
  if (p.tipo === "produto" || p.tipo === "pizza") return `${p.nome || "Produto"} grátis`;
  if (p.tipo === "desconto_valor") return `${reais(p.valor)} de desconto`;
  if (p.tipo === "desconto_pct") return `${String(p.valor || 0).replace(".", ",")}% de desconto${p.teto ? ` (até ${reais(p.teto)})` : ""}`;
  return "Frete grátis";
}

/** Custo que o prêmio tira do bolso da loja (para o custo médio por cartão). */
export function custoPremio(p) {
  if (p.custo != null && p.custo !== "") return Number(p.custo);
  if (p.tipo === "desconto_valor") return Number(p.valor || 0);
  return null;
}

// Chances iguais entre todos (a soma fecha em 100 no último)
const dividirIgual = (lista) => {
  const n = lista.length;
  if (!n) return lista;
  const parte = Math.floor(100 / n);
  return lista.map((p, i) => ({ ...p, chance: i === n - 1 ? 100 - parte * (n - 1) : parte }));
};

function Imagem({ p, grande = false }) {
  const t = doTipo(p.tipo);
  const Icone = t.icone;
  const tam = grande ? "w-14 h-14" : "w-12 h-12";
  if (p.foto) return <img src={miniaturaDe(p.foto, 128)} alt="" className={`${tam} rounded-xl object-cover bg-muted shrink-0`} />;
  return <span className={`${tam} rounded-xl bg-gradient-to-br ${t.cor} text-white grid place-items-center shrink-0`}><Icone className="w-6 h-6" /></span>;
}

function Campo({ rotulo, ajuda, children }) {
  return (
    <label className="block space-y-1">
      <span className="block text-xs font-medium">{rotulo}</span>
      <div className="[&>span]:flex [&>span]:w-full">{children}</div>
      {ajuda && <span className="block text-[11px] text-muted-foreground leading-snug">{ajuda}</span>}
    </label>
  );
}

/** Os campos do prêmio aberto para editar. */
function Editor({ p, produtos, varios, onChange }) {
  const [trocando, setTrocando] = useState(false);
  const produto = produtos.find((x) => x.id === p.product_id);
  const mudar = (campos) => onChange({ ...p, ...campos });
  const deProduto = p.tipo === "produto" || p.tipo === "pizza";
  const escolher = (x) => {
    setTrocando(false);
    if (p.tipo === "pizza") return mudar({ product_id: x.id, size_id: null, foto: x.foto, nome: "", valor: 0 });
    return mudar({ product_id: x.id, nome: `1 ${x.nome}`, valor: x.preco, foto: x.foto, custo: x.custo });
  };
  const escolherTamanho = (id) => {
    const t = (produto?.tamanhos || []).find((y) => y.id === id);
    mudar(t ? { size_id: t.id, nome: `1 Pizza ${t.nome}`, valor: t.preco } : { size_id: null });
  };

  // Produto/pizza: primeiro escolhe na lista; os campos aparecem depois
  if (deProduto && (trocando || !produto)) {
    return (
      <div className="mt-3 pt-3 border-t space-y-2">
        <p className="text-xs font-medium">{p.tipo === "pizza" ? "Escolha a pizza (a foto do cartão vem dela)" : "Escolha o produto do cardápio"}</p>
        <EscolherProduto produtos={produtos} pizza={p.tipo === "pizza"} escolhido={p.product_id} onEscolher={escolher} />
        {trocando && <button type="button" onClick={() => setTrocando(false)} className="text-xs font-semibold text-muted-foreground hover:text-foreground">Cancelar troca</button>}
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t space-y-3">
      {deProduto && (
        <div className="flex items-center gap-3 rounded-xl border bg-background p-2">
          {produto.foto
            ? <img src={miniaturaDe(produto.foto, 128)} alt="" className="w-10 h-10 rounded-lg object-cover" />
            : <span className="w-10 h-10 rounded-lg bg-muted grid place-items-center"><ImageOff className="w-4 h-4 text-muted-foreground" /></span>}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold truncate">{produto.nome}</span>
            <span className="block text-xs text-muted-foreground">{produto.categoria}{p.tipo === "produto" ? ` · ${BRL(produto.preco)}` : ""}</span>
          </span>
          <button type="button" onClick={() => setTrocando(true)} className="h-8 px-3 rounded-lg border text-xs font-semibold hover:bg-muted">Trocar</button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {p.tipo === "pizza" && produto && (
          <Campo rotulo="Tamanho da pizza" ajuda="O cliente escolhe o sabor; o prêmio vale este tamanho.">
            <select value={p.size_id || ""} onChange={(e) => escolherTamanho(e.target.value)} className="h-9 w-full rounded-lg border bg-background px-2.5 text-sm">
              <option value="">Escolha o tamanho</option>
              {(produto.tamanhos || []).map((t) => <option key={t.id} value={t.id}>{t.nome} — {BRL(t.preco)}</option>)}
            </select>
          </Campo>
        )}
        {deProduto && (
          <Campo rotulo="Nome no cartão">
            <input value={p.nome || ""} onChange={(e) => mudar({ nome: e.target.value })} maxLength={60}
              placeholder="Ex.: 1 X-Burger" className="h-9 w-full rounded-lg border bg-background px-2.5 text-sm" />
          </Campo>
        )}
        {deProduto && (
          <Campo rotulo="Vale até" ajuda="O desconto máximo do produto, se o preço dele subir.">
            <Dinheiro valor={p.valor} onChange={(v) => mudar({ valor: v })} largura="w-full" />
          </Campo>
        )}
        {p.tipo === "desconto_valor" && (
          <Campo rotulo="Desconto">
            <Dinheiro valor={p.valor} onChange={(v) => mudar({ valor: v })} largura="w-full" e2e="premio-valor" />
          </Campo>
        )}
        {p.tipo === "desconto_pct" && (
          <>
            <Campo rotulo="Desconto">
              <Numero valor={p.valor} onChange={(v) => mudar({ valor: v })} sufixo="%" max={100} largura="w-full" />
            </Campo>
            <Campo rotulo="Limite do desconto" ajuda="R$ 0,00 = sem limite.">
              <Dinheiro valor={p.teto} onChange={(v) => mudar({ teto: v })} largura="w-full" zeroVazio />
            </Campo>
          </>
        )}
        <Campo rotulo="Custo para a loja" ajuda="Quanto o prêmio custa para você. Entra no custo médio do cartão.">
          <Dinheiro valor={p.custo} onChange={(v) => mudar({ custo: v })} largura="w-full" zeroVazio />
        </Campo>
        {varios && (
          <Campo rotulo="Chance de sair" ajuda="Quando o cartão abre, o sistema sorteia um dos prêmios.">
            <Numero valor={p.chance} onChange={(v) => mudar({ chance: v })} sufixo="%" max={100} largura="w-full" />
          </Campo>
        )}
      </div>
    </div>
  );
}

function CartaoPremio({ p, produtos, varios, aberto, onAbrir, onChange, onRemover }) {
  const custo = custoPremio(p);
  return (
    <div className={`rounded-2xl border p-3 transition-colors ${aberto ? "border-primary/50 bg-primary/5" : "bg-muted/20"}`} data-e2e="premio-linha">
      <div className="flex items-center gap-3">
        <Imagem p={p} grande />
        <button type="button" onClick={onAbrir} className="min-w-0 flex-1 text-left">
          <span className="block font-semibold leading-tight truncate">{textoPremio(p) || "Escolha o produto"}</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            {TIPOS_PREMIO[p.tipo]}{custo != null ? ` · custa ${BRL(custo)} para a loja` : ""}
          </span>
          {varios && <span className="inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">{Number(p.chance || 0).toLocaleString("pt-BR")}% de chance</span>}
        </button>
        <button type="button" onClick={onAbrir} aria-label={aberto ? "Fechar" : "Editar"}
          className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted">
          {aberto ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
        </button>
        <button type="button" onClick={onRemover} aria-label="Tirar este prêmio"
          className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {aberto && <Editor p={p} produtos={produtos} varios={varios} onChange={onChange} />}
    </div>
  );
}

/** Escolha do tipo do prêmio novo, em blocos. */
function NovoPremio({ onEscolher, onCancelar }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-primary/40 p-3" data-e2e="premio-tipos">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold">Qual vai ser o prêmio?</p>
        <button type="button" onClick={onCancelar} aria-label="Cancelar" className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {TIPOS.map(({ tipo, icone: Icone, cor, sub }) => (
          <button key={tipo} type="button" onClick={() => onEscolher(tipo)} data-e2e={`premio-tipo-${tipo}`}
            className="rounded-xl border bg-background p-3 text-left hover:border-primary/60 hover:shadow-sm transition-all">
            <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${cor} text-white grid place-items-center`}><Icone className="w-5 h-5" /></span>
            <span className="block text-sm font-semibold mt-2 leading-tight">{TIPOS_PREMIO[tipo]}</span>
            <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Os prêmios do 10º carimbo (repaginado em 24/10/2026): cada um num cartão com a
 * foto do produto (ou o ícone do tipo), o texto que vai no cartão do cliente,
 * o custo e a chance. "Adicionar prêmio" abre os tipos em blocos; produto e
 * pizza abrem a lista do cardápio com fotos. Ao adicionar, as chances são
 * divididas igualmente (dá para ajustar no prêmio).
 */
export default function Premios({ premios, produtos, onChange }) {
  const [aberto, setAberto] = useState(null);
  const [novo, setNovo] = useState(false);
  const varios = premios.length > 1;
  const soma = premios.reduce((s, p) => s + Number(p.chance || 0), 0);
  const custos = premios.map(custoPremio);
  const custoMedio = !premios.length || custos.some((c) => c == null) ? null
    : varios ? (soma ? premios.reduce((s, p, i) => s + (Number(p.chance || 0) / soma) * custos[i], 0) : null) : custos[0];
  const mudar = (i, p) => onChange(premios.map((x, k) => (k === i ? p : x)));
  const remover = (i) => {
    const resto = premios.filter((_, k) => k !== i);
    const somaResto = resto.reduce((s, p) => s + Number(p.chance || 0), 0);
    onChange(Math.abs(somaResto - 100) > 0.01 ? dividirIgual(resto) : resto);
  };
  const criar = (tipo) => {
    const base = { id: novoId(), tipo, chance: 0 };
    const p = tipo === "desconto_valor" ? { ...base, valor: 20 } : tipo === "desconto_pct" ? { ...base, valor: 10 } : { ...base, valor: 0 };
    onChange(dividirIgual([...premios, p]));
    setAberto(p.id);
    setNovo(false);
  };

  return (
    <Secao numero={1} titulo="Prêmio do cartão" descricao="O que o cliente ganha ao completar os 10 carimbos. Com mais de um, o sistema sorteia um quando o cartão abre — e ele já aparece no cartão.">
      <div className="space-y-2.5">
        {!premios.length && !novo && (
          <p className="text-sm text-muted-foreground">Nenhum prêmio ainda. Adicione pelo menos um para ligar o cartão.</p>
        )}
        {premios.map((p, i) => (
          <CartaoPremio key={p.id} p={p} produtos={produtos} varios={varios} aberto={aberto === p.id}
            onAbrir={() => setAberto((x) => (x === p.id ? null : p.id))}
            onChange={(x) => mudar(i, x)} onRemover={() => remover(i)} />
        ))}
        {novo ? (
          <NovoPremio onEscolher={criar} onCancelar={() => setNovo(false)} />
        ) : (
          <button type="button" onClick={() => { setNovo(true); setAberto(null); }} data-e2e="premio-adicionar"
            className="w-full h-12 rounded-2xl border-2 border-dashed text-sm font-semibold text-muted-foreground hover:text-foreground hover:border-primary/50 inline-flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Adicionar prêmio
          </button>
        )}
      </div>

      {(varios || custoMedio != null) && (
        <div className="mt-3 rounded-xl bg-muted/40 px-3 py-2.5 space-y-1 text-sm">
          {varios && (
            <p className={`font-semibold flex items-center justify-between gap-2 ${Math.abs(soma - 100) > 0.01 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`}>
              <span>Soma das chances: {soma.toLocaleString("pt-BR")}%{Math.abs(soma - 100) > 0.01 ? " — precisa dar 100%" : ""}</span>
              {Math.abs(soma - 100) > 0.01 && (
                <button type="button" onClick={() => onChange(dividirIgual(premios))} className="text-xs font-semibold text-primary hover:underline">Dividir igual</button>
              )}
            </p>
          )}
          {custoMedio != null && (
            <p className="text-muted-foreground flex items-center gap-1.5">
              <Gift className="w-4 h-4 text-amber-500" /> Custo médio por cartão completo: <b className="text-foreground">{BRL(custoMedio)}</b> · por compra: <b className="text-foreground">{BRL(custoMedio / 8)}</b>
            </p>
          )}
        </div>
      )}
    </Secao>
  );
}
